import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonDebugging = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Professional Debugging (PDB)</h1>
                <p className="subtitle">Stop spraying print() statements. Learn to freeze time and inspect your code like a surgeon.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine a <strong>Murder Mystery</strong> (A Bug in your Code).
                    </p>
                    <ul>
                        <li><strong>Print Statements:</strong> You run into the room screaming "WHERE ARE YOU?" (Printing variables). If the killer is hiding, you miss him. It's messy and noisy.</li>
                        <li><strong>The Debugger (PDB):</strong> You are a CSI Detective with a <strong>Time Stone</strong>. You freeze time exactly when the murder happens (`breakpoint()`). You walk around the frozen room, look in pockets (`p variable`), check fingerprints (`dir()`), and then fast-forward time 1 second (`next`) to see what happens.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Magic Word: `breakpoint()`</h2>
                <p>
                    In Python 3.7+, you don't need to import anything. Just type `breakpoint()` anywhere, and your code will pause there.
                </p>
                <CodeBlock language="python" code={`def calculate_tax(income, rate):
    tax = income * rate
    breakpoint() # <--- PAUSE HERE
    return tax - 500

calculate_tax(50000, 0.3)`} />
                <p>
                    When you run this script, your terminal will change to `(Pdb)`. You are now in control.
                </p>
            </div>

            <div className="content-section">
                <h2>3. Essential PDB Commands</h2>
                <p>Memorize these 5 keys. They are your superpowers.</p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Key</th>
                            <th>Command</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>n</strong></td>
                            <td>next</td>
                            <td>Run the next line of code.</td>
                        </tr>
                        <tr>
                            <td><strong>s</strong></td>
                            <td>step</td>
                            <td>Step <strong>inside</strong> a function call.</td>
                        </tr>
                        <tr>
                            <td><strong>c</strong></td>
                            <td>continue</td>
                            <td>Resume execution until the next breakpoint.</td>
                        </tr>
                        <tr>
                            <td><strong>p</strong></td>
                            <td>print</td>
                            <td>Print a variable (e.g., `p income`).</td>
                        </tr>
                        <tr>
                            <td><strong>q</strong></td>
                            <td>quit</td>
                            <td>Exit the debugger immediately.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: Post-Mortem Debugging</h2>
                <p>
                    What if your script runs for 2 hours and then crashes? You can't sit there watching it!
                    <strong>Post-Mortem</strong> debugging opens the debugger <em>after</em> the crash, at the exact line of death.
                </p>
                <CodeBlock language="python" code={`# Run your script with this flag
python -m pdb -c continue myscript.py

# OR inside your code:
import pdb
import { ProTip } from '../../components/LessonUI';

try:
    complex_logic()
except Exception:
    # Opens PDB automatically when an error occurs!
    pdb.post_mortem()`} />
            </div>

            <div className="content-section">
                <h2>5. Inspecting the Unknown: `dir()` and `locals()`</h2>
                <p>
                    Inside PDB, you can run ANY Python code.
                </p>
                <ul>
                    <li>`locals()`: See every available variable in current scope.</li>
                    <li>`dir(obj)`: See all methods/attributes of an object.</li>
                    <li>`import math`: Yes, you can even import libraries mid-debug to calculate things!</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>6. Conditional Breakpoints</h2>
                <p>
                    Loop runs 1,000 times. Bug happens on number 999.
                    You don't want to hit `n` 999 times.
                </p>
                <CodeBlock language="python" code={`for i in range(1000):
    # Only stop if i is 999
    if i == 999:
        breakpoint()
    process(i)`} />
            </div>

        </div>
    );
};

export default PythonDebugging;

